import { Button, Group, Paper, Select, Stack, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IconArrowLeft } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";

interface CreateCustomerPayload {
  name: string;
  slug: string;
  ownerEmail: string;
  planKey: string;
  notes: string;
}

export const CustomersNewPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const form = useForm<CreateCustomerPayload>({
    initialValues: { name: "", slug: "", ownerEmail: "", planKey: "free", notes: "" },
    validate: {
      name: (v) => (v.trim().length > 0 ? null : "Required"),
      slug: (v) =>
        /^[a-z0-9-]+$/u.test(v.trim()) ? null : "Lowercase letters, numbers, hyphens only",
      ownerEmail: (v) => (/^\S+@\S+\.\S+$/u.test(v.trim()) ? null : "Valid email required")
    }
  });

  const create = useMutation({
    mutationFn: (values: CreateCustomerPayload) =>
      api.post<{ workspaceId: string; name: string }>("/internal/customers", {
        name: values.name.trim(),
        slug: values.slug.trim(),
        ownerEmail: values.ownerEmail.trim().toLowerCase(),
        planKey: values.planKey.trim() || "free",
        notes: values.notes.trim() || undefined
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["internal", "customers"] });
      notifications.show({ title: "Customer created", message: data.name, color: "green" });
      void navigate(`/customers/${data.workspaceId}`);
    },
    onError: (err: Error) => {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    }
  });

  return (
    <Stack gap="md" maw={560}>
      <Group>
        <Button
          component={Link as React.ElementType}
          to="/customers"
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          size="xs"
        >
          Customers
        </Button>
      </Group>

      <Title order={2}>New customer</Title>

      <Paper withBorder p="xl" radius="md">
        <form
          onSubmit={form.onSubmit((values) => {
            create.mutate(values);
          })}
        >
          <Stack gap="sm">
            <TextInput
              label="Company / workspace name"
              placeholder="Acme Corp"
              required
              {...form.getInputProps("name")}
            />
            <TextInput
              label="Slug"
              placeholder="acme-corp"
              description="Lowercase, hyphens only. Used in API identifiers."
              required
              {...form.getInputProps("slug")}
            />
            <TextInput
              label="Owner email"
              placeholder="admin@acmecorp.com"
              required
              {...form.getInputProps("ownerEmail")}
            />
            <Select
              label="Plan"
              description="Billing plan for this customer"
              data={[
                { value: "free", label: "Free" },
                { value: "starter", label: "Starter" },
                { value: "growth", label: "Growth" },
                { value: "enterprise", label: "Enterprise" }
              ]}
              {...form.getInputProps("planKey")}
            />
            <TextInput
              label="Notes"
              placeholder="Internal notes about this customer"
              {...form.getInputProps("notes")}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" component={Link as React.ElementType} to="/customers">
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create customer
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
};
